import {useParams} from 'react-router-dom'
import {parseISO} from 'date-fns'

import commonStyles from '../styles.module.scss'
import {
  MeterStat,
  Button,
  ProgressCircle,
  CentsMoney,
  Text,
} from 'common/components'
import {
  formatMoney,
  countPercentage,
  maybePluralize,
  formatDate,
  createReport,
  _differenceInCalendarDays,
} from 'common/helpers'
import {
  useGetProject,
  useGetProjectTransactions,
  TRANSACTION_NAME,
  Transaction,
} from '..'

export default function BorrowerProjectScheduleStats() {
  const params = useParams()
  const {data: project} = useGetProject(params.uuid)
  const {data: transactions} = useGetProjectTransactions(params.uuid)

  const noTransactions = !transactions.length

  const handleReport = noTransactions
    ? undefined
    : () =>
        createReport(
          getReportData(transactions),
          `Project schedule`,
          `project-schedule-report`
        )

  const today = new Date()
  const openDate = parseISO(project.openDate)
  const maturityDate = parseISO(project.maturityDate)
  const pastDays =
    today > openDate ? _differenceInCalendarDays(today, openDate) : 0
  const days = _differenceInCalendarDays(maturityDate, openDate)

  const balancePercent = countPercentage(
    project.balance,
    project.facilityLimit2
  )
  const repaidPercent = countPercentage(project.repaid, project.facilityLimit2, 2)

  return (
    <div className={commonStyles.borrowerProjectPanel}>
      <div>
        <Text size="4xl" mb={4}>
          {maybePluralize(pastDays, 'day')} passed
        </Text>
        <Text weight="medium" mb={16}>
          from {maybePluralize(days, 'day')} – {balancePercent}% of facility
          limit lended
        </Text>
        <Button disabled={noTransactions} onClick={handleReport}>
          Create report
        </Button>
      </div>
      <div className={commonStyles.borrowerProjectStats}>
        <MeterStat
          large
          type="secondGreen"
          title="Current balance"
          value={<CentsMoney value={project.balance} />}
          label={`${formatMoney(project.availableBalance, {
            cents: true,
          })} available`}
        />
        <MeterStat
          large
          type="darkGreen"
          title="Repayments"
          value={<CentsMoney value={project.repaid} color="green" />}
          label={`${repaidPercent.toFixed(2)}% repaid`}
        />
        <ProgressCircle
          green
          animation
          value={balancePercent}
          extraValue={repaidPercent}
          radius={74}
          strokeWidth={10}
        >
          <Text weight="medium" size="3xl" color="green">
            {balancePercent}%
          </Text>
        </ProgressCircle>
        <MeterStat
          large
          right
          type="grey"
          title="Facility limit 2"
          value={<CentsMoney value={project.facilityLimit2} />}
          label="including interest & fees"
        />
      </div>
    </div>
  )
}

const getReportData = (data: any[]) =>
  data.map((item, index) => ({
    '#': index + 1,
    Transaction: TRANSACTION_NAME[item.type as Transaction],
    Date: formatDate(item.date),
    Amount: item.amount,
    Notes: item.notes,
    Balance: item.balanceAfter,
  }))
